import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import { SimpleAspectRatio } from "../../../components/_shared-components/SimpleAspectRatio";
import { SwarovskiLink } from "../../../components/_shared-components/ui-elements/SwarovskiLink";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import React from "react";
import { SearchResultsViewSectionWithHeadline } from "./search-results-view-section-headline-with-children";
import { useSlice } from "../use-slice";
import { CSS_MQ } from "../../../styles/breakpoints";
import { pipe } from "fp-ts/lib/function";
import { nonEmptyArray, option } from "fp-ts";
import { SeeMoreLabel } from "./see-more-label";
import { ContentfulImage } from "../../../components/_shared-components/image-related/ContentfulImage";
import { ImageWithFocalPoint } from "../../../components/_shared-components/image-related/ImageWithFocalPoint";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../components/T";
import type { url } from "inspector";
type Props = {
  id: string;
  headline: TranslatedValue;
  tiles: Array<{
    key: string;
    image:
      | {
          url: string;
          focalPoint: undefined;
          sys: { id: string } | undefined;
        }
      | {
          sys: { id: string } | undefined;
          url: string;
          width: number;
          height: number;
          title: string;
          focalPoint: {
            x: number;
            y: number;
          };
        }
      | undefined;
    targetUrl: string;
    headline: string;
    subheadline: string | undefined;
    secondaryLabel: TranslatedValue | string | undefined;
  }>;
  NoSearchResultsFound: React.JSX.Element;
  Overlay?: React.JSX.Element;
  seeMoreLabel: TranslatedValue;
};

export const SearchResultsVisualViewSection: ReactTFC<Props> = props => {
  const slice = useSlice(props.tiles, 2);

  return (
    <SearchResultsViewSectionWithHeadline id={props.id} headline={props.headline}>
      {pipe(
        nonEmptyArray.fromArray(slice.slice),
        option.fold(
          () => props.NoSearchResultsFound,
          nea => (
            <div
              css={css`
                display: grid;

                grid-template-columns: 1fr;
                grid-gap: 20px;
                ${CSS_MQ.atLeastMd} {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                  grid-gap: 34px;
                }
              `}
            >
              {nea.map(tile => {
                return (
                  <SimpleAspectRatio
                    key={tile.key}
                    className={tile.image ? (tile.image.focalPoint ? "image-full" : "image-limited") : "no-image"}
                    atLeast={"md"}
                    css={css`
                      --aspect-ratio: 50/64;

                      ${CSS_MQ.atLeastLg} {
                        --aspect-ratio: 40/64;
                      }

                      position: relative;

                      &.image-full {
                        --shadow-height: 50%;
                        --alpha: 0.5;

                        &:after {
                          content: "";
                          position: absolute;
                          bottom: 0;
                          left: 0;
                          width: 100%;
                          height: var(--shadow-height);

                          z-index: 1;
                          border-radius: 8px;
                          pointer-events: none;

                          background: linear-gradient(0deg, rgba(0, 0, 0, var(--alpha)), rgba(0, 0, 0, 0));
                        }

                        @media (pointer: fine) {
                          img {
                            transition: transform 0.25s ease-out;
                            transform: scale(1);
                          }

                          &:hover {
                            img {
                              transition: transform 0.25s ease-out;
                              transform: scale(1.02);
                            }
                          }
                        }
                      }
                    `}
                  >
                    {props.Overlay}

                    <SwarovskiLink
                      isInternal={true}
                      scrollToTop={true}
                      link={tile.targetUrl}
                      styled={"no-decoration"}
                      attachStyles={linkStyles}
                    >
                      <div
                        css={css`
                          .image-limited & {
                            position: relative;
                          }

                          height: 200px;
                          ${CSS_MQ.atLeastMd} {
                            width: 100%;
                            height: 100%;
                          }
                        `}
                      >
                        {tile.image ? (
                          tile.image.focalPoint ? (
                            <ImageWithFocalPoint
                              assetId={undefined}
                              focalPoint={tile.image.focalPoint}
                              image={{
                                url: tile.image.url,
                                title: tile.image.title,
                                width: tile.image.width,
                                height: tile.image.height,
                              }}
                              requestedWidth={600}
                              lazyLoaded={"lazy"}
                              attachPictureStyles={css``}
                              attachStyles={css`
                                border-radius: 8px;
                              `}
                            />
                          ) : (
                            <ContentfulImage
                              assetId={tile.image.sys?.id}
                              contentfulLink={{
                                url: tile.image.url,
                                noTransparentBackground: true,
                                quality: 80,
                                requestedWidth: 600,
                              }}
                              css={imageStyles}
                              alt={""}
                            />
                          )
                        ) : (
                          <span />
                        )}
                      </div>

                      <div
                        css={css`
                          position: relative;
                          z-index: 2;

                          display: flex;
                          flex-direction: column;
                          justify-content: flex-start;
                          align-items: flex-start;

                          align-self: flex-start;

                          padding-top: 12px;

                          .image-full & {
                            --color-primary: ${CSS_COLORS.White};
                            --color-secondary: ${CSS_COLORS.White};
                          }

                          .image-limited & {
                            --color-primary: ${CSS_COLORS.Green};
                            --color-secondary: ${CSS_COLORS.Black};
                          }
                        `}
                      >
                        <span css={headlineStyles}>{tile.headline}</span>

                        {tile.subheadline && (
                          <span
                            className={"_subheadline"}
                            css={[
                              FONTS.SwarovskiFutura300,
                              fontSize.x2s,
                              css`
                                color: var(--color-primary);
                              `,
                            ]}
                          >
                            {tile.subheadline}
                          </span>
                        )}

                        {tile.secondaryLabel && (
                          <span
                            css={[
                              FONTS.SwarovskiFutura500,
                              fontSize.x2s,
                              css`
                                color: var(--color-secondary);

                                ._subheadline + & {
                                  margin-top: 10px;
                                }
                              `,
                            ]}
                          >
                            <T>{tile.secondaryLabel}</T>
                          </span>
                        )}
                      </div>
                    </SwarovskiLink>
                  </SimpleAspectRatio>
                );
              })}
            </div>
          ),
        ),
      )}

      {slice.shouldHaveLoadMoreButton && <SeeMoreLabel label={props.seeMoreLabel} onClick={slice.unslice} />}
    </SearchResultsViewSectionWithHeadline>
  );
};

const headlineStyles = [
  FONTS.SwarovskiFutura300,
  fontSize.x4,
  css`
    line-height: 1;
    color: var(--color-primary);
  `,
];

const imageStyles = css`
  object-fit: contain;
  object-position: center;

  justify-self: center;
  align-self: center;

  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
`;

const linkStyles = [
  fontColor.black,
  fontSize.x2s,
  css`
    .image-full & {
      ${CSS_MQ.atMostSm} {
        position: relative;
      }
      overflow: hidden;
    }

    display: flex;
    flex-direction: column;

    border: 1px solid #dcdcdc;
    border-radius: 8px;

    padding: 12px 20px;
    ${CSS_MQ.atLeastMd} {
      padding: 20px 30px;
    }
  `,
];
